import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const LINKS = [
  { id: "cases", key: "nav.cases" },
  { id: "services", key: "nav.services" },
  { id: "priser", key: "nav.pricing" },
  { id: "om-mig", key: "nav.about" },
  { id: "kontakt", key: "nav.contact" },
];

const LANGS = [
  { code: "da", label: "DA" },
  { code: "en", label: "EN" },
  { code: "sw", label: "SW" },
];

export const LanguageSwitch = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div data-testid="language-switcher" className="flex items-center rounded-full border border-white/15 p-1">
      {LANGS.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-switch-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors duration-300 ${
            lang === l.code ? "bg-[#FF3333] text-white" : "text-[#A3A3A3] hover:text-[#F2F2F2]"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Nav = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <header
      data-testid="main-nav"
      className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/40 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-syne text-lg font-800 font-extrabold tracking-tighter text-[#F2F2F2] transition-colors duration-300 hover:text-[#FF3333]"
        >
          NY HJEMMESIDE <span className="text-[#FF3333]">NU</span>
        </button>

        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors duration-300 hover:text-[#F2F2F2]"
            >
              {t(l.key)}
            </button>
          ))}
          <LanguageSwitch />
          <button
            data-testid="nav-cta-button"
            onClick={() => go("kontakt")}
            className="group flex items-center gap-2 rounded-full bg-[#FF3333] px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
          >
            {t("nav.cta")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          className="text-[#F2F2F2] md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-black/80 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-2 px-6 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="py-2 text-left font-syne text-2xl font-bold tracking-tight text-[#F2F2F2]"
                >
                  {t(l.key)}
                </button>
              ))}
              <div className="pt-3">
                <LanguageSwitch />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
